import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, Flex, Form, Input, Select, Space, Typography, message } from 'antd'
import { format, isValid, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import AuthSelectors from 'modules/domain/auth/selectors'
import { SeedPedigree } from 'modules/domain/seedTracker/types'
import { updateSeedInventory } from 'modules/domain/seedTracker/managers'
import { addNote } from 'modules/domain/note/managers'
import { NoteType } from 'types/entities'
import type { SeedInventory } from '../SeedInventoryTable'
import { mapSeedInventoryRecordToSeedInventoryRow } from '../SeedInventoryTable'
import { formatMt, formatMtAsNegative } from '../../formatMt'
import SeedInventoryNotes from './SeedInventoryNotes'
import {
  inputUnitSuffixTextStyle,
  labelPedigreeRowStyle,
  labelPedigreeRowLabelStyle,
  labelPedigreeValueWrapStyle,
  labelStyle,
  labelValueRowStyle,
  seedInventoryEditCardStyle,
  seedInventoryEditFieldStackStyle,
  seedInventoryEditFormStyle,
  seedInventoryEditGrowerDetailsCardStyle,
  seedInventoryEditInventoryNotesCardStyle,
  seedInventoryEditLastUpdatedStyle,
  seedInventoryEditReadOnlyRowsStyle,
  seedInventoryEditRowStyle,
  seedInventoryEditSecondRowStyle,
  seedInventoryEditStackedFieldStyle,
  seedInventoryEditTopBarStyle,
  valueStyle,
} from '../../styles'

type SeedInventoryEditFormValues = {
  crop_certificate_id?: string
  disposed_mt?: string
  on_hand_mt?: string
  clean_yield_actual_mt?: string
  clean_yield_estimate_mt?: string
  gross_yield_actual_bu?: string
  gross_yield_estimate_bu?: string
  initial_germ_actual?: string
  initial_germ_estimate?: string
  final_germ_actual?: string
  final_germ_estimate?: string
  diseased_actual?: string
  diseased_estimate?: string
  planned_seeded_area?: string
  current_seeded_area_actual?: string
  current_seeded_area_estimate?: string
  seeded_pedigree?: SeedPedigree
  projected_pedigree?: SeedPedigree
  projected_yield_actual_bu?: string
  projected_yield_estimate_bu?: string
}

type SeedInventoryEditProps = {
  record: SeedInventory
  onCancel: () => void
  onSaved: (updated: SeedInventory) => void
}

const pedigreeOptions = Object.values(SeedPedigree).map((v) => ({ label: v, value: v }))
const emptyMetric = '—'

const OPTIONAL_KEYS: (keyof SeedInventoryEditFormValues)[] = [
  'crop_certificate_id',
  'disposed_mt',
  'on_hand_mt',
  'clean_yield_actual_mt',
  'clean_yield_estimate_mt',
  'gross_yield_actual_bu',
  'gross_yield_estimate_bu',
  'initial_germ_actual',
  'initial_germ_estimate',
  'final_germ_actual',
  'final_germ_estimate',
  'diseased_actual',
  'diseased_estimate',
  'planned_seeded_area',
  'current_seeded_area_actual',
  'current_seeded_area_estimate',
  'projected_yield_actual_bu',
  'projected_yield_estimate_bu',
]

const CARD_ROW_GAP_PX = 16

function LabelValueRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Flex justify="space-between" align="center" style={labelValueRowStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value}</span>
    </Flex>
  )
}

const PedigreeLabel = styled.div<{ backgroundColor: string }>`
  display: flex;
  height: 22px;
  padding: 0 12px;
  align-items: center;
  gap: 8px;
  border-radius: 12px;
  background: ${(props) => props.backgroundColor};
  color: white;
  font-size: 12px;
  width: fit-content;
`

const getPedigreeBackgroundColor = (pedigree: SeedPedigree): string => {
  switch (pedigree) {
    case SeedPedigree.Certified:
      return '#20B02E'
    case SeedPedigree.Registered:
      return '#5C3700'
    case SeedPedigree.Foundation:
      return '#FFAB2E'
    case SeedPedigree.Select:
      return '#037BFF'
    case SeedPedigree.Breeder:
      return '#776D6D'
    default:
      return '#776D6D'
  }
}

function LabelPedigreeRow({ label, pedigree }: { label: string; pedigree: SeedPedigree }) {
  return (
    <Flex vertical gap={0} style={labelPedigreeRowStyle}>
      <span style={labelPedigreeRowLabelStyle}>{label}</span>
      <span style={labelPedigreeValueWrapStyle}>
        <PedigreeLabel backgroundColor={getPedigreeBackgroundColor(pedigree)}>{pedigree}</PedigreeLabel>
      </span>
    </Flex>
  )
}

function formatLastUpdatedDate(dateStr: string): string {
  const parsed = parseISO(dateStr)
  const date = isValid(parsed) ? parsed : new Date(dateStr)
  return isValid(date) ? format(date, 'dd MMMM yyyy') : dateStr
}

const InputUnitSuffix: React.FC<{ unit: string }> = ({ unit }) => (
  <Typography.Text type="secondary" style={inputUnitSuffixTextStyle}>
    {unit}
  </Typography.Text>
)

function normalizeMetric(v?: string): string | undefined {
  if (!v || v.trim() === '' || v.trim() === emptyMetric) {
    return undefined
  }
  return v.trim()
}

function toInitialValues(record: SeedInventory): SeedInventoryEditFormValues {
  return {
    crop_certificate_id: normalizeMetric(record.cropCertificateId),
    disposed_mt: normalizeMetric(record.disposedMT),
    on_hand_mt: normalizeMetric(record.onHandMT),
    clean_yield_actual_mt: normalizeMetric(record.cleanYieldActualMT),
    clean_yield_estimate_mt: normalizeMetric(record.cleanYieldEstimateMT),
    gross_yield_actual_bu: normalizeMetric(record.grossYieldActualBU),
    gross_yield_estimate_bu: normalizeMetric(record.grossYieldEstimateBU),
    initial_germ_actual: normalizeMetric(record.initialGermActual),
    initial_germ_estimate: normalizeMetric(record.initialGermEstimate),
    final_germ_actual: normalizeMetric(record.finalGermActual),
    final_germ_estimate: normalizeMetric(record.finalGermEstimate),
    diseased_actual: normalizeMetric(record.diseasedActual),
    diseased_estimate: normalizeMetric(record.diseasedEstimate),
    planned_seeded_area: normalizeMetric(record.plannedSeededArea),
    current_seeded_area_actual: normalizeMetric(record.currentSeededAreaActual),
    current_seeded_area_estimate: normalizeMetric(record.currentSeededAreaEstimate),
    seeded_pedigree: record.seededPedigree,
    projected_pedigree: record.projectedPedigree,
    projected_yield_actual_bu: normalizeMetric(record.projectedYieldActualBU),
    projected_yield_estimate_bu: normalizeMetric(record.projectedYieldEstimateBU),
  }
}

function buildUpdateBody(values: SeedInventoryEditFormValues): Record<string, unknown> {
  const body: Record<string, unknown> = {
    seeded_pedigree: values.seeded_pedigree,
    projected_pedigree: values.projected_pedigree,
  }

  for (const key of OPTIONAL_KEYS) {
    const raw = values[key]
    if (typeof raw === 'string' && raw.trim() !== '') {
      body[key] = raw.trim()
    }
  }
  return body
}

const SeedInventoryEdit: React.FC<SeedInventoryEditProps> = ({ record, onCancel, onSaved }) => {
  const { t } = useTranslation(['seedTracker', 'common', 'validation', 'errors'])
  const profile = useSelector(AuthSelectors.profile)
  const [form] = Form.useForm<SeedInventoryEditFormValues>()
  const [submitting, setSubmitting] = useState(false)
  const [pendingNoteText, setPendingNoteText] = useState('')

  useEffect(() => {
    form.setFieldsValue(toInitialValues(record))
  }, [form, record])

  const handleFinish = useCallback(
    async (values: SeedInventoryEditFormValues) => {
      setSubmitting(true)
      try {
        const updated = await updateSeedInventory(record.id, buildUpdateBody(values))
        const noteText = pendingNoteText.trim()
        const createdBy = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
        if (noteText) {
          try {
            await addNote({
              parent_id: updated.id,
              note_type: NoteType.SeedInventoryNote,
              note_text: noteText,
              created_by: createdBy || undefined,
            })
            setPendingNoteText('')
          } catch {
            void message.error('Inventory saved, but the note could not be saved.')
          }
        }
        onSaved(mapSeedInventoryRecordToSeedInventoryRow(updated))
        message.success(t('common:save'))
      } catch {
        message.error(t('unknown_error', { ns: 'errors' }))
      } finally {
        setSubmitting(false)
      }
    },
    [onSaved, pendingNoteText, profile?.first_name, profile?.last_name, record.id, t],
  )

  const requiredMsg = t('field_required', { ns: 'validation' })

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} style={seedInventoryEditFormStyle}>
      <Flex justify="space-between" align="center" style={seedInventoryEditTopBarStyle}>
        {record.lastUpdatedDate ? (
          <div style={seedInventoryEditLastUpdatedStyle}>
            {t('seedTracker:seedInventoryFields.lastUpdatedOn')} {formatLastUpdatedDate(record.lastUpdatedDate)}
          </div>
        ) : (
          <div />
        )}
        <Space>
          <Button onClick={onCancel} size="large">
            {t('common:cancel')}
          </Button>
          <Button type="primary" htmlType="submit" size="large" loading={submitting}>
            {t('common:save')}
          </Button>
        </Space>
      </Flex>

      <Flex gap={CARD_ROW_GAP_PX} style={seedInventoryEditRowStyle}>
        <Card
          title={t('seedTracker:drawerPanels.seedInventory')}
          variant="borderless"
          style={seedInventoryEditCardStyle}
        >
          <Form.Item
            name="crop_certificate_id"
            label={t('seedTracker:seedInventoryFields.cropCertificateId')}
            style={seedInventoryEditStackedFieldStyle}
          >
            <Input size="large" />
          </Form.Item>
          <Flex vertical gap={0} style={seedInventoryEditReadOnlyRowsStyle}>
            <LabelValueRow
              label={t('seedTracker:seedInventoryFields.available')}
              value={`${formatMt(record.availableMT)} ${t('seedTracker:seedInventoryFields.units.mt')}`}
            />
            <LabelValueRow
              label={t('seedTracker:seedInventoryFields.pendingSale')}
              value={`${formatMtAsNegative(record.pendingSaleMT)} ${t('seedTracker:seedInventoryFields.units.mt')}`}
            />
          </Flex>
          <Flex vertical gap="small" style={seedInventoryEditFieldStackStyle}>
            <Form.Item
              name="disposed_mt"
              label={t('seedTracker:addInventory.fields.disposed')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input size="large" suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.mt')} />} />
            </Form.Item>
            <Form.Item
              name="on_hand_mt"
              label={t('seedTracker:addInventory.fields.onHand')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input size="large" suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.mt')} />} />
            </Form.Item>
            <Form.Item
              name="clean_yield_actual_mt"
              label={t('seedTracker:addInventory.fields.cleanYieldAct')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input size="large" suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.mt')} />} />
            </Form.Item>
            <Form.Item
              name="clean_yield_estimate_mt"
              label={t('seedTracker:addInventory.fields.cleanYieldEst')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input size="large" suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.mt')} />} />
            </Form.Item>
            <Form.Item
              name="gross_yield_actual_bu"
              label={t('seedTracker:addInventory.fields.grossYieldAct')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input size="large" suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.bu')} />} />
            </Form.Item>
            <Form.Item
              name="gross_yield_estimate_bu"
              label={t('seedTracker:addInventory.fields.grossYieldEst')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input size="large" suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.bu')} />} />
            </Form.Item>
          </Flex>
        </Card>

        <Card
          title={t('seedTracker:drawerPanels.seedInventoryQuality')}
          variant="borderless"
          style={seedInventoryEditCardStyle}
        >
          <Flex vertical gap="small">
            <Form.Item
              name="initial_germ_actual"
              label={t('seedTracker:addInventory.fields.initialGerminatedAct')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input
                size="large"
                suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.percentage')} />}
              />
            </Form.Item>
            <Form.Item
              name="initial_germ_estimate"
              label={t('seedTracker:addInventory.fields.initialGerminatedEst')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input
                size="large"
                suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.percentage')} />}
              />
            </Form.Item>
            <Form.Item
              name="final_germ_actual"
              label={t('seedTracker:addInventory.fields.finalGerminatedAct')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input
                size="large"
                suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.percentage')} />}
              />
            </Form.Item>
            <Form.Item
              name="final_germ_estimate"
              label={t('seedTracker:addInventory.fields.finalGerminatedEst')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input
                size="large"
                suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.percentage')} />}
              />
            </Form.Item>
            <Form.Item
              name="diseased_actual"
              label={t('seedTracker:addInventory.fields.diseasedAct')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input
                size="large"
                suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.percentage')} />}
              />
            </Form.Item>
            <Form.Item
              name="diseased_estimate"
              label={t('seedTracker:addInventory.fields.diseasedEst')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input
                size="large"
                suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.percentage')} />}
              />
            </Form.Item>
          </Flex>
        </Card>

        <Card
          title={t('seedTracker:drawerPanels.seedingIntentions')}
          variant="borderless"
          style={seedInventoryEditCardStyle}
        >
          <Flex vertical gap="small">
            <Form.Item style={seedInventoryEditStackedFieldStyle}>
              <LabelPedigreeRow
                label={t('seedTracker:addInventory.fields.seededPedigree')}
                pedigree={record.seededPedigree}
              />
            </Form.Item>
            <Form.Item
              name="projected_pedigree"
              label={t('seedTracker:addInventory.fields.projectedPedigree')}
              style={seedInventoryEditStackedFieldStyle}
              rules={[{ required: true, message: requiredMsg }]}
            >
              <Select allowClear options={pedigreeOptions} size="large" />
            </Form.Item>
            <Form.Item
              name="planned_seeded_area"
              label={t('seedTracker:addInventory.fields.plannedSeedArea')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input
                size="large"
                suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.acres')} />}
              />
            </Form.Item>
            <Form.Item
              name="current_seeded_area_actual"
              label={t('seedTracker:addInventory.fields.currentSeededAreaAct')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input
                size="large"
                suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.acres')} />}
              />
            </Form.Item>
            <Form.Item
              name="current_seeded_area_estimate"
              label={t('seedTracker:addInventory.fields.currentSeededAreaEst')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input
                size="large"
                suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.acres')} />}
              />
            </Form.Item>
            <Form.Item
              name="projected_yield_actual_bu"
              label={t('seedTracker:addInventory.fields.projectedYieldAct')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input size="large" suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.bu')} />} />
            </Form.Item>
            <Form.Item
              name="projected_yield_estimate_bu"
              label={t('seedTracker:addInventory.fields.projectedYieldEst')}
              style={seedInventoryEditStackedFieldStyle}
            >
              <Input size="large" suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.bu')} />} />
            </Form.Item>
          </Flex>
        </Card>
      </Flex>

      <Flex gap={CARD_ROW_GAP_PX} style={seedInventoryEditSecondRowStyle(CARD_ROW_GAP_PX)}>
        <Card
          title={t('seedTracker:drawerPanels.growerDetails')}
          variant="borderless"
          style={seedInventoryEditGrowerDetailsCardStyle(CARD_ROW_GAP_PX)}
        >
          <Flex vertical gap="small">
            <LabelValueRow label={t('seedTracker:detail.growerFields.grower')} value={record.grower} />
            {/* <LabelValueRow label={t('seedTracker:detail.growerFields.location')} value={record.location} /> */}
            <LabelValueRow label={t('seedTracker:detail.growerFields.region')} value={record.region} />
            <LabelValueRow label={t('seedTracker:detail.growerFields.contactName')} value={record.contactName} />
            <LabelValueRow label={t('seedTracker:detail.growerFields.contactNumber')} value={record.contactNumber} />
            <LabelValueRow label={t('seedTracker:detail.growerFields.contactEmail')} value={record.contactEmail} />
          </Flex>
        </Card>
        <Card
          title={t('seedTracker:drawerPanels.inventoryNotes')}
          variant="borderless"
          style={seedInventoryEditInventoryNotesCardStyle(CARD_ROW_GAP_PX)}
        >
          <SeedInventoryNotes
            record={record}
            editMode={true}
            draftNoteText={pendingNoteText}
            onDraftNoteTextChange={setPendingNoteText}
          />
        </Card>
      </Flex>
    </Form>
  )
}

export default SeedInventoryEdit
